import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Checkbox, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, Biotech as LabIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadLabCategoriesbd, getLabInsurances,
  updateInsuredLab, deleteInsuredLab, saveNewLab,
  bulkApplyLabInsurance, moveLabToInsurancedb
} from '../BaseURL';
import {
  AdminPageHead, ADMIN, EmptyState, BulkEditBar,
  adminFieldSx, brandButtonSx,
} from './adminUi';

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// ============================================================================
//  Laboratory Setup
// ============================================================================
//
//  Converted to mirror Diagnosis Setup, which is the same job on a different
//  catalogue: pick an item on the left, price it per insurance scheme on the
//  right. They were two different screens for one workflow.
//
//  WHAT WAS WRONG, and it is the same list Services and Diagnosis carried:
//
//    · a teal→lime gradient (#11998e → #38ef7d) on the page banner, both dialog
//      headers and the table head — a fourth palette, on a screen sitting inside
//      a navy admin shell
//    · `height: 600` on both panels, so the cards stopped short of the content
//      and left a band of canvas beneath them
//    · `<Grid item xs={12} md={4} size={4}>` throughout. In MUI 7 that API is
//      silently IGNORED — no error, no warning — so both panels were rendering
//      full-width and stacking rather than sitting side by side
//    · a raw <select> for the insurance filter, styled by hand, next to MUI
//      fields
//
//  AND THE FUNCTIONAL GAP. The only bulk action was "tick rows, then copy one to
//  OTHER schemes" — which adds this test to schemes that do not have it. There
//  was no way to change a tariff across the schemes that DO. That is the common
//  case at tariff review, and it is what BulkEditBar is for.

/* ── Shared styles ──────────────────────────────────────── */
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: '#DDE5EF' },
    '&:hover fieldset': { borderColor: '#123A6D' },
    '&.Mui-focused fieldset': { borderColor: '#123A6D' },
  },
};

// The bulk-edit bar's fields. Blank means "leave this column alone", so the
// starting state is all-blank and pressing Apply with nothing typed is a no-op.
const EMPTY_BULK = { cost: '', toup: '', unin: '', G_DRG: '' };

const BULK_FIELDS = [
  { key: 'cost',  label: 'Tariff',    type: 'number', adornment: 'GH¢' },
  { key: 'toup',  label: 'Top-up',    type: 'number', adornment: 'GH¢' },
  { key: 'unin',  label: 'Uninsured', type: 'number', adornment: 'GH¢' },
  { key: 'G_DRG', label: 'DRG Code',  placeholder: 'e.g. INVE122' },
];

/* ── Editable Row ───────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid #f0f0f0', py: 0.75, px: 1 };
  const rowId = `${row.trans_code}-${row.status}`;
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)': { background: selected ? 'rgba(17,153,142,0.10)' : '#f9faff' },
      '&:nth-of-type(even)': { background: selected ? 'rgba(17,153,142,0.10)' : '#fff' },
      '&:hover': { background: selected ? 'rgba(17,153,142,0.15)' : 'rgba(17,153,142,0.04)' },
      transition: 'background 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox size="small" checked={selected} onChange={() => onSelectToggle(rowId)}
          sx={{ color: '#123A6D', '&.Mui-checked': { color: '#123A6D' }, p: 0.5 }} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: '#93A2B8', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 150 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0F1F3D' }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.cost ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('cost', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.toup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('toup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.unin ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('unin', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.G_DRG ?? ''} onChange={e => onChange('G_DRG', e.target.value)} sx={cellInputSx} />
      </TableCell>
      {/*  Icon buttons, 96px rather than 180. Same as Diagnosis and Procedure —
           two labelled buttons were what pushed Actions past the right edge of
           the panel. The tooltip and the aria-label carry the word. */}
      <TableCell sx={{ ...tdSx, width: 96, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="Save this row" arrow>
            <span>
              <IconButton size="small" onClick={() => onUpdate(row)} disabled={updating || deleting}
                aria-label={`Save ${row.status || 'this scheme'}`}
                sx={{ color: '#fff', background: '#217A3C', borderRadius: '7px', p: .6,
                  '&:hover': { background: '#1B6631' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
                {updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete this row" arrow>
            <span>
              <IconButton size="small" onClick={() => onDelete(row)} disabled={deleting || updating}
                aria-label={`Delete ${row.status || 'this scheme'}`}
                sx={{ color: '#fff', background: '#D64545', borderRadius: '7px', p: .6,
                  '&:hover': { background: '#B23A3A' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
                {deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};




const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingStatuses, selectedRows }) => {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [editCost, setEditCost] = useState('');
  const [editToup, setEditToup] = useState('');
  const [editUnin, setEditUnin] = useState('');
  const [editDRG, setEditDRG] = useState('');

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

 useEffect(() => {
    if (open && sourceRow) {
      // Pre-check all insurances matching the selected rows
      const preChecked = {};
      if (selectedRows?.length > 0) {
        selectedRows.forEach(row => {
          const match = insuranceProviders.find(p => p.name === row.status);
          if (match) preChecked[match.id] = true;
        });
      }
      setSelectedInsurances(preChecked);
      setInsuranceSearch('');
    }
  }, [open, sourceRow, selectedRows, insuranceProviders]);

  const handleInsuranceToggle = (id) => setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
  };
  const handleClose = () => { setSelectedInsurances({}); setInsuranceSearch(''); onClose(); };
  const getProviderName = (id) => insuranceProviders.find(p => String(p.id) === String(id))?.name || '';

  const handleUpdate = async () => {
    if (selectedCount === 0) { await Swal.fire({ title: 'No Selection', text: 'Select at least one insurance.', icon: 'warning', confirmButtonColor: '#123A6D' }); return; }
    if (editCost === '' || editToup === '' || editUnin === '') { await Swal.fire({ title: 'Validation', text: 'Fill in all price fields.', icon: 'warning', confirmButtonColor: '#123A6D' }); return; }
    const confirm = await Swal.fire({
      title: 'Apply Pricing?', html: `Apply new values to <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}?`,
      icon: 'question', showCancelButton: true, confirmButtonColor: '#123A6D', cancelButtonColor: '#93A2B8', confirmButtonText: 'Yes, Apply'
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => ({
        trans_code: sourceRow.trans_code, lab: sourceRow.lab,
        cost: parseFloat(editCost), toup: parseFloat(editToup),
        unin: parseFloat(editUnin), G_DRG: editDRG.trim(),
        status: getProviderName(id),
      }));
      const response = await axios.post(bulkApplyLabInsurance, { rows });
      if (response.data.success) {
        await Swal.fire({ title: 'Applied!', html: `<b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''} updated.`, icon: 'success', timer: 2500, showConfirmButton: false, timerProgressBar: true });
        if (onSaved) onSaved(); handleClose();
      } else throw new Error(response.data.message || 'Apply failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (selectedCount === 0) { await Swal.fire({ title: 'No Selection', text: 'Select at least one insurance to delete.', icon: 'warning', confirmButtonColor: '#123A6D' }); return; }
    const confirm = await Swal.fire({
      title: 'Delete Records?', html: `Delete pricing for <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}?`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#D64545', cancelButtonColor: '#93A2B8', confirmButtonText: 'Yes, Delete'
    });
    if (!confirm.isConfirmed) return;
    setDeleting(true);
    try {
      const promises = selectedInsuranceIds.map(id =>
        axios.post(deleteInsuredLab, { trans_code: sourceRow.trans_code, lab: sourceRow.lab, status: getProviderName(id) })
      );
      await Promise.all(promises);
      await Swal.fire({ title: 'Deleted!', html: `<b>${selectedCount}</b> record${selectedCount !== 1 ? 's' : ''} deleted.`, icon: 'success', timer: 2000, showConfirmButton: false, timerProgressBar: true });
      if (onSaved) onSaved(); handleClose();
    } catch (err) { await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setDeleting(false); }
  };

  const filteredProviders = insuranceProviders.filter(i => i.name?.toLowerCase().includes(insuranceSearch.toLowerCase()));

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ background: 'linear-gradient(135deg,#123A6D 0%,#0B2452 100%)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CopyIcon sx={{ fontSize: 26 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 16 }}>Apply to Insurance Providers</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{sourceRow.lab}</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/*  CSS Grid. This was <Grid container>/<Grid item xs md>, which MUI 7
            ignores — so the two halves stacked instead of sitting side by side. */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '5fr 7fr' },
          alignItems: 'stretch',
        }}>

          {/* LEFT: Edit fields + action buttons */}
          <Box sx={{ p: 3, minWidth: 0, borderRight: { md: '1px solid #DDE5EF' }, borderBottom: { xs: '1px solid #DDE5EF', md: 0 }, background: '#F4F7FB', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#5A6B87', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>Edit Values</Typography>
              <Typography variant="caption" sx={{ color: '#93A2B8' }}>Will apply to all selected insurances on the right</Typography>
            </Box>
            <TextField fullWidth size="small" label="Tariff GH¢" type="number"
              value={editCost} onChange={e => setEditCost(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="Top-up GH¢" type="number"
              value={editToup} onChange={e => setEditToup(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="Uninsured GH¢" type="number"
              value={editUnin} onChange={e => setEditUnin(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="DRG Code (optional)"
              value={editDRG} onChange={e => setEditDRG(e.target.value)}
              placeholder="e.g., INVE122" sx={cellInputSx} />
            <Divider />
            <Button fullWidth variant="contained" onClick={handleUpdate}
              disabled={saving || deleting || selectedCount === 0}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ ...brandButtonSx, py: 1 }}>
              {saving ? 'Updating…' : `Update ${selectedCount > 0 ? `${selectedCount} ` : ''}Insurance${selectedCount !== 1 ? 's' : ''}`}
            </Button>
            <Button fullWidth variant="contained" onClick={handleDelete}
              disabled={deleting || saving || selectedCount === 0}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
              sx={{ background: '#D64545', color: 'white', fontWeight: 700, textTransform: 'none', py: 1, boxShadow: 'none', '&:hover': { background: '#B23A3A' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
              {deleting ? 'Deleting…' : `Delete ${selectedCount > 0 ? `${selectedCount} ` : ''}Insurance${selectedCount !== 1 ? 's' : ''}`}
            </Button>
            {selectedCount === 0 && <Typography variant="caption" sx={{ color: '#D64545', textAlign: 'center', fontSize: '0.7rem' }}>Select at least one insurance on the right</Typography>}
          </Box>

          {/* RIGHT: All insurance providers */}
          <Box sx={{ p: 3, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#5A6B87', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                Select Insurances
              </Typography>
              {selectedCount > 0 && <Chip size="small" label={`${selectedCount} selected`} sx={{ background: '#E7F5EC', color: '#217A3C', fontWeight: 600, fontSize: '0.7rem' }} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ borderColor: '#DDE5EF', color: '#123A6D', fontSize: '0.72rem', py: 0.4, textTransform: 'none' }}>Select All</Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedInsurances({})} sx={{ borderColor: '#DDE5EF', color: '#123A6D', fontSize: '0.72rem', py: 0.4, textTransform: 'none' }}>Deselect All</Button>
            </Box>
            <TextField fullWidth size="small" placeholder="Search insurance…" value={insuranceSearch}
              onChange={e => setInsuranceSearch(e.target.value)} sx={{ ...adminFieldSx, mb: 1.5 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#93A2B8' }} /></InputAdornment> }} />
            <Box sx={{ flex: 1, overflowY: 'auto', border: '1px solid #DDE5EF', borderRadius: ADMIN.radiusSm, minHeight: 300,
              '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
              {loadingInsurance
                ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                : filteredProviders.length === 0
                  ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                      <Typography variant="body2" color="text.secondary">No providers found</Typography>
                    </Box>
                  : <Box sx={{ p: 1 }}>
                      {filteredProviders.map(ins => (
                        <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)}
                          sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1, cursor: 'pointer', mb: 0.5,
                            background: selectedInsurances[ins.id] ? '#E8EEF7' : 'transparent',
                            border: `1px solid ${selectedInsurances[ins.id] ? '#C7D8EE' : 'transparent'}`,
                            '&:hover': { background: 'rgba(18,58,109,0.05)' } }}>
                          <Checkbox size="small" checked={!!selectedInsurances[ins.id]}
                            onClick={e => e.stopPropagation()} onChange={() => handleInsuranceToggle(ins.id)}
                            sx={{ p: 0.5, color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' } }} />
                          <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem' }}>{ins.name}</Typography>
                          {existingStatuses.includes(ins.name) && (
                            <Chip label="Exists" size="small" sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, background: '#E7F5EC', color: '#217A3C' }} />
                          )}
                        </Box>
                      ))}
                    </Box>
              }
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #DDE5EF', background: '#F4F7FB' }}>
        <Button onClick={handleClose} variant="outlined" disabled={saving || deleting} sx={{ textTransform: 'none', color: '#5A6B87', borderColor: '#DDE5EF' }}>Close</Button>
      </DialogActions>
    </Dialog>



  );

  
};

/* ── New Lab Pricing Dialog ─────────────────────────────── */
const NewLabPricingDialog = ({ open, onClose, selectedLab, insuranceProviders, loadingInsurance, onSaved, existingStatuses }) => {
  const [saving, setSaving] = useState(false);
  const [cost, setCost] = useState('');
  const [topUp, setTopUp] = useState('');
  const [uninsured, setUninsured] = useState('');
  const [drgCode, setDrgCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;
  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

useEffect(() => {
  if (open) {
    const preChecked = {};
    insuranceProviders.forEach(i => {
      if (existingStatuses.includes(i.name)) preChecked[i.id] = true;
    });
    setCost(''); setTopUp(''); setUninsured(''); setDrgCode('');
    setSelectedInsurances(preChecked); setInsuranceSearch(''); setErrors({});
  }
}, [open, insuranceProviders, existingStatuses]);


  const handleInsuranceToggle = (id) => { setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] })); clearError('insurance'); };
  const handleSelectAll = () => { const all = {}; insuranceProviders.forEach(i => { if (!existingStatuses.includes(i.name)) all[i.id] = true; }); setSelectedInsurances(all); clearError('insurance'); };
  const handleClose = () => { setCost(''); setTopUp(''); setUninsured(''); setDrgCode(''); setSelectedInsurances({}); setInsuranceSearch(''); setErrors({}); onClose(); };

  const validate = () => {
    const e = {};
    if (cost === '' || cost === null) e.cost = 'Required'; else if (isNaN(parseFloat(cost)) || parseFloat(cost) < 0) e.cost = 'Invalid';
    if (topUp === '' || topUp === null) e.topUp = 'Required'; else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0) e.topUp = 'Invalid';
    if (uninsured === '' || uninsured === null) e.uninsured = 'Required'; else if (isNaN(parseFloat(uninsured)) || parseFloat(uninsured) < 0) e.uninsured = 'Invalid';
    if (selectedCount === 0) e.insurance = 'Select at least one insurance';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return { trans_code: selectedLab.lab, lab: selectedLab.lab, cost: parseFloat(cost), toup: parseFloat(topUp), unin: parseFloat(uninsured), G_DRG: drgCode.trim(), status: provider?.name || '' };
      });
      const response = await axios.post(saveNewLab, { rows });
      if (response.data.success) {
        await Swal.fire({ title: 'Saved!', html: `<b>${selectedLab.lab}</b> priced for <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}.`, icon: 'success', timer: 2500, showConfirmButton: false, timerProgressBar: true });
        if (onSaved) onSaved(); handleClose();
      } else throw new Error(response.data.message || 'Save failed');
    } catch (err) {
      await Swal.fire({ title: 'Save Failed', text: err.response?.data?.message || err.message, icon: 'error' });
    } finally { setSaving(false); }
  };

  const filteredProviders = insuranceProviders.filter(i => i.name?.toLowerCase().includes(insuranceSearch.toLowerCase()));
  const availableProviders = filteredProviders.filter(i => !existingStatuses.includes(i.name));
  const alreadyAssigned = filteredProviders.filter(i => existingStatuses.includes(i.name));

  if (!selectedLab) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade} PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ background: 'linear-gradient(135deg,#123A6D 0%,#0B2452 100%)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LabIcon sx={{ fontSize: 26 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 16 }}>New Insurance Pricing</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{selectedLab.lab}</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>

        {/*  CSS Grid, not <Grid item xs md size>. MUI 7 ignores that API outright,
            which is why this dialog rendered as one full-width column with the
            pricing fields stacked above the scheme picker rather than beside it. */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '5fr 7fr' },
          alignItems: 'stretch',
        }}>

          <Box sx={{ p: 3, minWidth: 0, borderRight: { md: '1px solid #DDE5EF' }, borderBottom: { xs: '1px solid #DDE5EF', md: 0 }, background: '#F4F7FB' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#5A6B87', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>Set Pricing</Typography>
            <TextField fullWidth size="small" label="Tariff  GH¢ *" type="number" value={cost} onChange={e => { setCost(e.target.value); clearError('cost'); }} error={!!errors.cost} helperText={errors.cost} inputProps={{ min: 0, step: '0.01' }} sx={{ ...adminFieldSx, mb: 2 }} />
            <TextField fullWidth size="small" label="Top-up GH¢ *" type="number" value={topUp} onChange={e => { setTopUp(e.target.value); clearError('topUp'); }} error={!!errors.topUp} helperText={errors.topUp} inputProps={{ min: 0, step: '0.01' }} sx={{ ...adminFieldSx, mb: 2 }} />
            <TextField fullWidth size="small" label="Uninsured GH¢ *" type="number" value={uninsured} onChange={e => { setUninsured(e.target.value); clearError('uninsured'); }} error={!!errors.uninsured} helperText={errors.uninsured} inputProps={{ min: 0, step: '0.01' }} sx={{ ...adminFieldSx, mb: 2 }} />
            <Divider sx={{ my: 1.5 }} />
            <TextField fullWidth size="small" label="DRG Code (optional)" value={drgCode} onChange={e => setDrgCode(e.target.value)} sx={{ ...adminFieldSx, mb: 2 }} placeholder="e.g., INVE122" />
            <Alert severity="info" sx={{ fontSize: '0.72rem', mt: 1, borderRadius: ADMIN.radiusSm }}><strong>Tariff:</strong> base insurance tariff<br /><strong>Top-up:</strong> additional patient fee<br /><strong>Uninsured:</strong> non-insured price</Alert>
          </Box>


          <Box sx={{ p: 3, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: errors.insurance ? '#D64545' : '#5A6B87', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>Select Insurance Providers *</Typography>
                <Typography variant="caption" color={errors.insurance ? 'error' : 'text.secondary'}>{selectedCount} selected | {availableProviders.length} available</Typography>
              </Box>
              {selectedCount > 0 && <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label={`${selectedCount}`} sx={{ background: '#E7F5EC', color: '#217A3C', fontWeight: 600, fontSize: '0.7rem' }} />}
            </Box>
            {errors.insurance && <Typography variant="caption" sx={{ color: '#D64545', fontSize: '0.7rem', mb: 0.5 }}>{errors.insurance}</Typography>}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ borderColor: '#123A6D', color: '#123A6D', fontSize: '0.72rem', py: 0.4 }}>Select All Available</Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedInsurances({})} sx={{ borderColor: '#123A6D', color: '#123A6D', fontSize: '0.72rem', py: 0.4 }}>Deselect All</Button>
            </Box>
            <TextField fullWidth size="small" placeholder="Search insurance..." value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#93A2B8' }} /></InputAdornment> }} />
            <Box sx={{ flex: 1, overflowY: 'auto', border: `1px solid ${errors.insurance ? '#D64545' : '#DDE5EF'}`, borderRadius: 2, p: 1, minHeight: 200, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
              {loadingInsurance ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box> : (
                <>
                  {availableProviders.length > 0 && <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#217A3C', display: 'block', mb: 0.5, px: 0.5 }}>Available ({availableProviders.length})</Typography>
                    {availableProviders.map(ins => (
                      <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)} sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1, cursor: 'pointer', mb: 0.5, background: selectedInsurances[ins.id] ? '#E8EEF7' : 'transparent', border: selectedInsurances[ins.id] ? '1px solid #C7D8EE' : '1px solid transparent', '&:hover': { background: 'rgba(18,58,109,0.05)' } }}>
                        <Checkbox size="small" checked={!!selectedInsurances[ins.id]} onClick={e => e.stopPropagation()} onChange={() => handleInsuranceToggle(ins.id)} sx={{ p: 0.5, color: '#123A6D', '&.Mui-checked': { color: '#123A6D' } }} />
                        <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem' }}>{ins.name}</Typography>
                      </Box>
                    ))}
                  </Box>}
                  
{alreadyAssigned.length > 0 && <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
  <Typography variant="caption" sx={{ fontWeight: 600, color: '#D64545', display: 'block', mb: 0.5, px: 0.5 }}>
    Already Priced ({alreadyAssigned.length}) — uncheck to skip
  </Typography>
  {alreadyAssigned.map(ins => (
    <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)}
      sx={{
        display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
        cursor: 'pointer', mb: 0.5,
        background: selectedInsurances[ins.id] ? 'rgba(229,57,53,0.06)' : 'transparent',
        border: selectedInsurances[ins.id] ? '1px solid rgba(229,57,53,0.25)' : '1px solid transparent',
        '&:hover': { background: 'rgba(229,57,53,0.04)' },
      }}>
      <Checkbox size="small"
        checked={!!selectedInsurances[ins.id]}
        onClick={e => e.stopPropagation()}
        onChange={() => handleInsuranceToggle(ins.id)}
        sx={{ p: 0.5, color: '#D64545', '&.Mui-checked': { color: '#D64545' } }} />
      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#0F1F3D' }}>{ins.name}</Typography>
      <Chip label="Exists" size="small"
        sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, background: '#FDECEC', color: '#B23A3A' }} />
    </Box>
  ))}
</Box>}

                  {availableProviders.length === 0 && alreadyAssigned.length === 0 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}><Typography variant="body2" color="text.secondary">No providers found</Typography></Box>}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #DDE5EF', background: '#F4F7FB', gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" disabled={saving} sx={{ textTransform: 'none', color: '#5A6B87', borderColor: '#DDE5EF' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ ...brandButtonSx, px: 3 }}>
          {saving ? 'Saving…' : 'Save Pricing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Main Component ─────────────────────────────────────── */
const Laboratory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [labGroups, setLabGroups] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const [insuranceRows, setInsuranceRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [newPricingDialogOpen, setNewPricingDialogOpen] = useState(false);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);


  // ── Move lab tests state ──────────────────────────────────
  const [selectMode, setSelectMode]                     = useState(false);
  const [selectedLabNames, setSelectedLabNames]         = useState(new Set());
  const [moveDialogOpen, setMoveDialogOpen]             = useState(false);
  const [moveTargetInsurance, setMoveTargetInsurance]   = useState('');
  const [moving, setMoving]                             = useState(false);

  const [insuranceFilter, setInsuranceFilter] = useState('');

  // Bulk-edit bar state. See handleBarApply.
  const [bulkValues, setBulkValues] = useState(EMPTY_BULK);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try { const res = await axios.get(loadInsurance); if (res.data.success) setInsuranceProviders(res.data.results); else setError('Failed to load insurance'); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load insurance'); }
    finally { setLoadingInsurance(false); }
  };

  const loadLabGroups = async () => {
    setLoadingLabs(true); setError(null);
    try { const res = await axios.get(loadLabCategoriesbd); if (res.data.success) setLabGroups(res.data.results); else setError('Failed to load lab tests'); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load lab tests'); }
    finally { setLoadingLabs(false); }
  };

  const loadInsuranceRecords = async (lab) => {
    setLoadingRecords(true); setInsuranceRows([]); setSelectedRowIds([]);
    try { const res = await axios.post(getLabInsurances, { categoryName: lab.lab }); if (res.data.success) setInsuranceRows(res.data.results); }
    catch (err) { await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load records', icon: 'error' }); }
    finally { setLoadingRecords(false); }
  };

  useEffect(() => { loadInsuranceProviders(); loadLabGroups(); }, []);

  const handleLabSelect = async (lab) => {
    if (selectedLab?.lab === lab.lab) { setSelectedLab(null); setInsuranceRows([]); setSelectedRowIds([]); return; }
    setSelectedLab(lab);
    await loadInsuranceRecords(lab);
  };

  const handleRowChange = (idx, field, value) => setInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const handleUpdateRow = async (row) => {
    if (row.cost === '' || row.toup === '' || row.unin === '') {
       await Swal.fire({ title: 'Validation', text: 'Fill in all price fields', icon: 'warning' }); return; }
    if (!row.trans_code) { await Swal.fire({ title: 'Error', text: 'Transaction code missing', icon: 'error' }); return; }
    const rowId = `${row.trans_code}-${row.status}`;
    setUpdatingRowId(rowId);
    try {
      const res = await axios.post(updateInsuredLab, { trans_code: row.trans_code, lab: row.lab, status: row.status, cost: parseFloat(row.cost), toup: parseFloat(row.toup), unin: parseFloat(row.unin), G_DRG: row.G_DRG || '' });
      if (res.data.success) await Swal.fire({ title: 'Updated!', text: `${row.lab} — ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
      else throw new Error(res.data.message || 'Update failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.trans_code) return;
    const result = await Swal.fire({ title: 'Delete?', html: `Delete pricing for <strong>${row.status}</strong>?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#D64545', cancelButtonColor: '#93A2B8', confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    const rowId = `${row.trans_code}-${row.status}`;
    setDeletingRowId(rowId);
    try {
      const res = await axios.post(deleteInsuredLab, { trans_code: row.trans_code, lab: row.lab, status: row.status });
      if (res.data.success) { await Swal.fire({ title: 'Deleted!', icon: 'success', timer: 1500, showConfirmButton: false }); setInsuranceRows(prev => prev.filter(r => !(r.trans_code === row.trans_code && r.status === row.status))); setSelectedRowIds(prev => prev.filter(id => id !== rowId)); }
      else throw new Error(res.data.message || 'Delete failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setDeletingRowId(null); }
  };

  const getRowId = (row) => `${row.trans_code}-${row.status}`;
  const handleRowSelectionToggle = (id) => setSelectedRowIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSelectAllRows = () => { if (selectedRowIds.length === insuranceRows.length) setSelectedRowIds([]); else setSelectedRowIds(insuranceRows.map(r => getRowId(r))); };

 const handleBulkApply = () => {
  if (selectedRowIds.length === 0) {
    Swal.fire({ title: 'No Selection', text: 'Select at least one row.', icon: 'info', confirmButtonColor: '#123A6D' });
    return;
  }
  const sourceRow = insuranceRows.find(r => getRowId(r) === selectedRowIds[0]);
  if (!sourceRow) return;
  setSourceRowForBulkApply(sourceRow);
  setBulkApplyDialogOpen(true);
};

  /* ─────────────────────────────────────────────────────────────
     Bulk edit bar — apply typed values / delete, across ticked rows
     ───────────────────────────────────────────────────────────── */
  //
  //  The half this screen never had. "Apply to Insurances" ADDS this test to
  //  schemes that do not carry it; this EDITS the ones that do. At a tariff
  //  review the second is the whole job, and it was six rows retyped by hand.
  //
  //  Both loop the EXISTING single-row endpoints rather than calling a new bulk
  //  one — the same call the Update button on each row makes, already validated
  //  and in daily use. The number of schemes on one test is single figures, so a
  //  bulk SQL path would be faster and completely untested, which is the wrong
  //  trade on records that price claims.
  //
  //  Failures are counted rather than thrown, and the row list is RELOADED at the
  //  end. If scheme 3 of 6 fails, the first two really did change; showing the
  //  optimistic values for all six would be a lie about what is stored.
  const bulkTargets = () => insuranceRows.filter(r => selectedRowIds.includes(getRowId(r)));

  const handleBarApply = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    // Blank means "leave alone". Only what was actually typed is applied.
    const patch = {};
    for (const k of ['cost', 'toup', 'unin', 'G_DRG']) {
      if (String(bulkValues[k] ?? '').trim() !== '') patch[k] = String(bulkValues[k]).trim();
    }
    if (Object.keys(patch).length === 0) return;

    const LABELS = { cost: 'Tariff', toup: 'Top-up', unin: 'Uninsured', G_DRG: 'DRG Code' };
    const confirm = await Swal.fire({
      title: `Apply to ${targets.length} scheme${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${Object.entries(patch).map(([k, v]) => `<div><b>${LABELS[k]}</b> → ${v}</div>`).join('')}
               <div style="margin-top:8px;color:#5A6B87">Fields left blank keep their current values.</div>
             </div>`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#123A6D', cancelButtonColor: '#5A6B87',
      confirmButtonText: 'Yes, apply',
    });
    if (!confirm.isConfirmed) return;

    setBulkApplying(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        // The endpoint writes every column it receives, so the row's CURRENT
        // values are sent for anything the patch does not cover — otherwise a
        // blank field here would wipe the stored one.
        const res = await axios.post(updateInsuredLab, {
          trans_code: row.trans_code,
          lab:        row.lab,
          status:     row.status,
          cost:       parseFloat(patch.cost ?? row.cost),
          toup:       parseFloat(patch.toup ?? row.toup),
          unin:       parseFloat(patch.unin ?? row.unin),
          G_DRG:      patch.G_DRG ?? row.G_DRG ?? '',
        });
        if (res.data.success) ok++; else failed.push(row.status || row.trans_code);
      } catch (_) { failed.push(row.status || row.trans_code); }
    }
    setBulkApplying(false);

    if (selectedLab) await loadInsuranceRecords(selectedLab);
    setBulkValues(EMPTY_BULK);

    await Swal.fire({
      title: failed.length === 0 ? 'Applied' : 'Partly applied',
      text: failed.length === 0
        ? `${ok} scheme${ok === 1 ? '' : 's'} updated.`
        : `${ok} updated. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };

  const handleBarDelete = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    const confirm = await Swal.fire({
      title: `Delete ${targets.length} record${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${targets.map(r => `<div>• ${r.status || r.trans_code}</div>`).join('')}
               <div style="margin-top:8px;color:#B23A3A">This cannot be undone.</div>
             </div>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#D64545', cancelButtonColor: '#5A6B87',
      confirmButtonText: `Yes, delete ${targets.length}`,
    });
    if (!confirm.isConfirmed) return;

    setBulkDeleting(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        const res = await axios.post(deleteInsuredLab, {
          trans_code: row.trans_code, lab: row.lab, status: row.status,
        });
        if (res.data.success) ok++; else failed.push(row.status || row.trans_code);
      } catch (_) { failed.push(row.status || row.trans_code); }
    }
    setBulkDeleting(false);
    setSelectedRowIds([]);

    if (selectedLab) await loadInsuranceRecords(selectedLab);

    await Swal.fire({
      title: failed.length === 0 ? 'Deleted' : 'Partly deleted',
      text: failed.length === 0
        ? `${ok} record${ok === 1 ? '' : 's'} removed.`
        : `${ok} removed. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };

const filteredLabs = labGroups
  .filter((l, idx, arr) => arr.findIndex(x => x.lab === l.lab) === idx)
  .filter(l => l.lab?.toLowerCase().includes(searchTerm.toLowerCase()))
  .filter(l => !insuranceFilter || l.insurances?.some(i => i.name === insuranceFilter));

const existingStatuses = insuranceRows.map(r => r.status);


  const toggleLabSelection = (labName) => {
  setSelectedLabNames(prev => {
    const next = new Set(prev);
    next.has(labName) ? next.delete(labName) : next.add(labName);
    return next;
  });
};

const handleSelectAllVisibleLabs = (visibleLabs) => {
  setSelectedLabNames(new Set(visibleLabs.map(l => l.lab)));
};

const handleDeselectAllLabs = () => setSelectedLabNames(new Set());

const handleMoveLabs = async () => {
  if (selectedLabNames.size === 0 || !moveTargetInsurance) return;
  setMoving(true);
  try {
    const res = await axios.post(moveLabToInsurancedb, {
      labNames:        Array.from(selectedLabNames),
      targetInsurance: moveTargetInsurance,
    });
    if (res.data.success) {
      await Swal.fire({
        title: 'Done!',
        text: res.data.message,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        timerProgressBar: true,
      });
      setMoveDialogOpen(false);
      setSelectedLabNames(new Set());
      setMoveTargetInsurance('');
      setSelectMode(false);
      loadLabGroups();
    } else {
      throw new Error(res.data.message);
    }
  } catch (err) {
    await Swal.fire({ title: 'Move Failed', text: err.response?.data?.message || err.message, icon: 'error' });
  } finally {
    setMoving(false);
  }
};

  return (
    <>
      {/* One navy page header, replacing the full-width teal→lime band and the
          two "contained" buttons sitting on it. Same treatment as Diagnosis. */}
      <AdminPageHead
        icon={LabIcon}
        title="Laboratory Setup"
        subtitle="Lab test tariffs, top-ups and uninsured prices, per insurance scheme"
        actions={
          <>
            <Tooltip title="Reload lab tests and schemes" arrow>
              <span>
                <IconButton
                  onClick={() => { loadLabGroups(); loadInsuranceProviders(); }}
                  disabled={loadingLabs} size="small" aria-label="Reload lab tests"
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                >
                  {loadingLabs ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained" startIcon={<AddIcon />}
              onClick={() => {
                if (!selectedLab) {
                  Swal.fire({ title: 'Select a lab test', text: 'Click a lab test on the left first.', icon: 'info', confirmButtonColor: '#123A6D' });
                  return;
                }
                setNewPricingDialogOpen(true);
              }}
              sx={{ ...brandButtonSx, py: .85, px: 2, fontSize: 13, whiteSpace: 'nowrap' }}
            >
              New Pricing
            </Button>
          </>
        }
      />

      <Box>
        <Box>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: ADMIN.radiusSm }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Two panels on a REAL CSS grid. This was `<Grid item xs md size>`,
              which MUI 7 ignores outright — so both panels were full-width and
              stacked. Both also carried `height: 600`, a fixed height regardless
              of content, which is what left the band of canvas underneath. */}
          <Box sx={{
            display: 'grid',
            gap: { xs: 2, md: 2.5 },
            alignItems: 'start',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '340px minmax(0, 1fr)' },
          }}>

            {/* Left: lab test list.
                Sticky and full-height from md up, so the card fills the space
                beside the records panel instead of stopping short. */}
            <Box sx={{
              minWidth: 0,
              position: { md: 'sticky' },
              top: { md: ADMIN.stickyTop.sm },
              height: { md: `calc(100vh - ${ADMIN.stickyTop.sm}px - 16px)` },
            }}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #DDE5EF', borderRadius: ADMIN.radius, background: '#fff', display: 'flex', flexDirection: 'column', height: { md: '100%' }, overflow: 'hidden' }}>

  {/*  Insurance filter. Was a raw <select> styled by hand with four inline
      colour literals, sitting directly above an MUI TextField — two different
      form controls, a few pixels apart, on the same card. */}
  <Select
    fullWidth size="small" displayEmpty
    value={insuranceFilter}
    onChange={e => setInsuranceFilter(e.target.value)}
    sx={{ ...adminFieldSx, mb: 1.5, fontSize: 12.5,
      '& .MuiSelect-select': { py: '8px' } }}
    renderValue={(v) => (v || <span style={{ color: '#93A2B8' }}>All insurances</span>)}
  >
    <MenuItem value="">All insurances</MenuItem>
    {insuranceProviders.map(ins => (
      <MenuItem key={ins.id} value={ins.name} sx={{ fontSize: 12.5 }}>{ins.name}</MenuItem>
    ))}
  </Select>

{/* Active filter chip */}
{insuranceFilter && (
  <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Chip size="small"
      icon={<InsuranceIcon sx={{ fontSize: '13px !important' }} />}
      label={insuranceFilter}
      onDelete={() => setInsuranceFilter('')}
      sx={{ fontSize: '0.72rem', bgcolor: '#E8EEF7', color: '#123A6D', fontWeight: 600, height: 22 }} />
    <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#93A2B8' }}>
      — filtering tests
    </Typography>
  </Box>
)}

{/* Search */}
<TextField fullWidth size="small" placeholder="Search lab tests…" value={searchTerm}
  onChange={e => setSearchTerm(e.target.value)} sx={{ ...adminFieldSx, mb: 1.5 }}
  InputProps={{
    startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#93A2B8' }} /></InputAdornment>),
    endAdornment: searchTerm ? (
      <InputAdornment position="end">
        <IconButton size="small" aria-label="Clear search" onClick={() => setSearchTerm('')}>
          <CloseIcon sx={{ fontSize: 15, color: '#93A2B8' }} />
        </IconButton>
      </InputAdornment>
    ) : null,
  }} />

  {/* ── Header row with Move toggle ── */}
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D' }}>Available Lab Tests</Typography>
      {loadingLabs && <CircularProgress size={16} />}
      <Chip size="small" label={`${filteredLabs.length} total`} sx={{ fontSize: '0.68rem', background: '#E8EEF7', color: '#123A6D' }} />
    </Box>
    <Tooltip title={selectMode ? 'Exit selection mode' : 'Select tests to copy to another insurance'}>
      <Button size="small" variant={selectMode ? 'contained' : 'outlined'}
        startIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
        onClick={() => { setSelectMode(s => !s); setSelectedLabNames(new Set()); }}
        sx={{
          fontSize: '0.68rem', py: 0.3, px: 1, textTransform: 'none', flexShrink: 0,
          borderColor: '#123A6D', color: selectMode ? '#fff' : '#123A6D',
          bgcolor: selectMode ? '#123A6D' : 'transparent',
          '&:hover': { bgcolor: selectMode ? '#0B2452' : '#E8EEF7', borderColor: '#123A6D' }
        }}>
        {selectMode ? 'Cancel' : 'Move'}
      </Button>
    </Tooltip>
  </Box>

  {/* ── Selection action bar ── */}
  {selectMode && (
    <Box sx={{ mb: 1, p: 1, borderRadius: '8px', bgcolor: '#E8EEF7', border: '1px solid #C7D8EE', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#123A6D', fontSize: '0.72rem' }}>
        {selectedLabNames.size} selected
      </Typography>
      <Button size="small" variant="text"
        onClick={() => handleSelectAllVisibleLabs(filteredLabs)}
        sx={{ fontSize: '0.68rem', py: 0.2, color: '#123A6D', minWidth: 0, textTransform: 'none' }}>
        All
      </Button>
      <Button size="small" variant="text" onClick={handleDeselectAllLabs}
        sx={{ fontSize: '0.68rem', py: 0.2, color: '#93A2B8', minWidth: 0, textTransform: 'none' }}>
        None
      </Button>
      {selectedLabNames.size > 0 && (
        <Button size="small" variant="contained"
          startIcon={<SwapHorizIcon sx={{ fontSize: 13 }} />}
          onClick={() => setMoveDialogOpen(true)}
          sx={{ ml: 'auto', fontSize: '0.68rem', py: 0.3, px: 1.2, textTransform: 'none', bgcolor: '#123A6D', '&:hover': { bgcolor: '#0B2452' } }}>
          Move {selectedLabNames.size}
        </Button>
      )}
    </Box>
  )}

  {/* ── Selected lab indicator (only in normal mode) ── */}
  {selectedLab && !selectMode && (
    <Box sx={{ mb: 1.5, p: 1, borderRadius: '8px', background: '#E7F5EC', border: '1px solid #2E9E4F', display: 'flex', alignItems: 'center', gap: 1 }}>
      <CheckCircleIcon sx={{ fontSize: 15, color: '#2E9E4F', flexShrink: 0 }} />
      <Typography noWrap sx={{ fontSize: 11.5, color: '#217A3C', fontWeight: 700, minWidth: 0 }}>{selectedLab.lab}</Typography>
      <IconButton size="small" aria-label="Clear selection" onClick={() => { setSelectedLab(null); setInsuranceRows([]); setSelectedRowIds([]); }} sx={{ ml: 'auto', p: 0.2, flexShrink: 0 }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  )}

  {/* `minHeight: 0` lets this flex child shrink so it scrolls inside the card
      instead of pushing it past the viewport. */}
  <Box sx={{
    flex: { md: 1 }, minHeight: 0,
    maxHeight: { xs: 420, md: 'none' },
    overflowY: 'auto', pr: 0.5,
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
  }}>
    {loadingLabs
      ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}><CircularProgress size={22} /></Box>
      : filteredLabs.length === 0
        ? <EmptyState
            icon={ScienceIcon}
            title="No lab tests"
            body={searchTerm || insuranceFilter
              ? 'Nothing matches the current search or filter.'
              : 'Nothing loaded yet — use Reload in the header.'}
          />
        : filteredLabs.map((lab, idx) => {
            const isSelected = selectedLab?.lab === lab.lab && !selectMode;
            const isChecked  = selectedLabNames.has(lab.lab);
            const on = isSelected || isChecked;
            return (
              <Box key={`${lab.lab}-${idx}`}
                onClick={() => selectMode ? toggleLabSelection(lab.lab) : handleLabSelect(lab)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, mb: .375, px: 1, py: .875,
                  borderRadius: '8px', cursor: 'pointer', minWidth: 0,
                  transition: 'background-color .15s ease, border-color .15s ease',
                  border: `1px solid ${on ? '#123A6D' : 'transparent'}`,
                  background: on ? '#E8EEF7' : 'transparent',
                  // No translateX on hover. With hundreds of rows, every row
                  // shifting sideways under the pointer made the list feel
                  // unstable to scan.
                  '&:hover': { background: on ? '#E8EEF7' : 'rgba(18,58,109,0.05)' },
                }}>
                {selectMode ? (
                  <Checkbox size="small" checked={isChecked}
                    onChange={() => toggleLabSelection(lab.lab)}
                    onClick={e => e.stopPropagation()}
                    sx={{ p: 0.3, color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' }, flexShrink: 0 }} />
                ) : (
                  <ScienceIcon sx={{ fontSize: 15, flexShrink: 0, color: on ? '#123A6D' : '#93A2B8' }} />
                )}
                <Typography noWrap title={lab.lab}
                  sx={{ fontSize: 12.5, color: on ? '#123A6D' : '#0F1F3D', fontWeight: on ? 700 : 500, minWidth: 0 }}>
                  {lab.lab}
                </Typography>
              </Box>
            );
          })
    }
  </Box>
</Paper>
            </Box>

            {/* RIGHT: Insurance records */}
            <Box sx={{ minWidth: 0 }}>
              <Paper elevation={0} sx={{ border: '1px solid #DDE5EF', borderRadius: ADMIN.radius, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #DDE5EF', background: '#F4F7FB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D' }}>Insurance Coverage Records</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedLab ? `Records for: ${selectedLab.lab}` : 'Select a lab test to load records'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && <Chip size="small" label={`${selectedRowIds.length} selected`} sx={{ background: '#e3f2fd', color: '#1976d2', fontWeight: 600, fontSize: '0.7rem' }} />}
                    {selectedLab && !loadingRecords && <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />} label={`${insuranceRows.length} record${insuranceRows.length !== 1 ? 's' : ''}`} size="small" sx={{ background: insuranceRows.length > 0 ? '#E7F5EC' : '#FDECEC', color: insuranceRows.length > 0 ? '#217A3C' : '#B23A3A', fontWeight: 600, fontSize: '0.7rem' }} />}
                  </Box>
                </Box>

                {/* Bulk edit bar. Type once, apply to every ticked scheme.
                    Replaces a strip whose only bulk action was "copy this row to
                    OTHER schemes" — which cannot help you when the schemes that
                    already have the test are the ones whose tariff moved. */}
                {selectedLab && insuranceRows.length > 0 && (
                  <BulkEditBar
                    fields={BULK_FIELDS}
                    values={bulkValues}
                    onChange={(k, v) => setBulkValues(prev => ({ ...prev, [k]: v }))}
                    selectedCount={selectedRowIds.length}
                    totalCount={insuranceRows.length}
                    onApply={handleBarApply}
                    onDelete={handleBarDelete}
                    applying={bulkApplying}
                    deleting={bulkDeleting}
                    itemLabel="scheme"
                  />
                )}

                {/* The row-as-template flow, kept as a secondary action. It does a
                    DIFFERENT job — it adds this test to schemes that do not have
                    it yet, where the bar edits rows that already exist. */}
                {selectedLab && insuranceRows.length > 0 && (
                  <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid #DDE5EF', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
                    <Button
                      size="small" variant="text" startIcon={<CopyIcon sx={{ fontSize: 15 }} />}
                      onClick={handleBulkApply}
                      disabled={selectedRowIds.length === 0}
                      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#123A6D', py: .3 }}
                    >
                      Copy to other schemes
                    </Button>
                    <Typography sx={{ fontSize: 11, color: '#93A2B8' }}>
                      {selectedRowIds.length === 0
                        ? 'Tick a row to use it as the template'
                        : 'Adds this test to schemes that do not have it yet'}
                    </Typography>
                  </Box>
                )}

                {/* Three placeholder states that each filled a 600px panel with a
                    faded pastel icon, so the card stayed tall with nothing in it. */}
                {!selectedLab ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="Pick a lab test"
                    body="Choose one from the list on the left to see and edit the price each scheme is charged."
                  />
                ) : loadingRecords ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, gap: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: '#123A6D' }} />
                    <Typography sx={{ fontSize: 13, color: '#5A6B87' }}>Loading records…</Typography>
                  </Box>
                ) : insuranceRows.length === 0 ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="No schemes on this test"
                    body={`Nothing is priced for ${selectedLab.lab} yet — use New Pricing in the header.`}
                  />
                ) : (
                  <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
                    <Table size="small" sx={{ minWidth: 820 }}>
                      <TableHead>
                        <TableRow>
                          {/* Was a solid teal band with white text — heavier than
                              the data under it, and the third green on the screen.
                              Quiet uppercase labels on the canvas tint, matching
                              every other admin table. */}
                          <TableCell sx={{ background: '#F4F7FB', borderBottom: '1px solid #DDE5EF', width: 48, textAlign: 'center', py: 1.25, px: 0.5 }}>
                            <Checkbox size="small" checked={selectedRowIds.length === insuranceRows.length && insuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < insuranceRows.length}
                              onChange={handleSelectAllRows} sx={{ color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' }, '&.MuiCheckbox-indeterminate': { color: '#2E9E4F' }, p: 0.5 }} />
                          </TableCell>
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'Uninsured', 'DRG Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ background: '#F4F7FB', color: '#93A2B8', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid #DDE5EF', py: 1.25, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 96, textAlign: 'center' }) }}>{label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {insuranceRows.map((row, idx) => {
                          const rowId = getRowId(row);
                          return (
                            <EditableRow key={`${rowId}-${idx}`} row={row} idx={idx}
                              selected={selectedRowIds.includes(rowId)} onSelectToggle={handleRowSelectionToggle}
                              onChange={(field, value) => handleRowChange(idx, field, value)}
                              onUpdate={handleUpdateRow} onDelete={handleDeleteRow}
                              updating={updatingRowId === rowId} deleting={deletingRowId === rowId} />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>



      {/* ══════════ Move Lab Tests Dialog ══════════ */}
<Dialog open={moveDialogOpen} onClose={() => !moving && setMoveDialogOpen(false)}
  maxWidth="xs" fullWidth TransitionComponent={Fade}
  PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

  <DialogTitle sx={{ p: 0 }}>
    <Box sx={{ background: 'linear-gradient(135deg,#123A6D 0%,#0B2452 100%)', color: 'white', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SwapHorizIcon sx={{ fontSize: 26 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>Copy Lab Tests</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {selectedLabNames.size} test{selectedLabNames.size !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={() => setMoveDialogOpen(false)} disabled={moving} sx={{ color: 'white' }}>
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>

  <DialogContent sx={{ pt: 3, pb: 1 }}>

    {/* Selected labs chips */}
    <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: '#F4F7FB', border: '1px solid #c6e9df', maxHeight: 130, overflowY: 'auto' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#0B2452', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', mb: 1 }}>
        Lab tests to copy:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {Array.from(selectedLabNames).map(name => (
          <Chip key={name} label={name} size="small"
            sx={{ fontSize: '0.72rem', height: 20, bgcolor: '#E8EEF7', color: '#0B2452' }} />
        ))}
      </Box>
    </Box>

    {/* Target insurance picker */}
    <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.75 }}>
      Copy to Insurance *
    </Typography>
    <Box sx={{ border: `1px solid ${moveTargetInsurance ? '#123A6D' : '#DDE5EF'}`, borderRadius: 1.5, overflow: 'hidden', maxHeight: 220, overflowY: 'auto',
      '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
      {insuranceProviders.map(ins => (
        <Box key={ins.id}
          onClick={() => setMoveTargetInsurance(ins.name)}
          sx={{
            display: 'flex', alignItems: 'center', px: 2, py: 1,
            cursor: 'pointer', transition: 'all 0.15s',
            background: moveTargetInsurance === ins.name ? '#E8EEF7' : 'transparent',
            borderLeft: moveTargetInsurance === ins.name ? '3px solid #123A6D' : '3px solid transparent',
            '&:hover': { background: 'rgba(18,58,109,0.05)' }
          }}>
          <InsuranceIcon sx={{ fontSize: 15, color: moveTargetInsurance === ins.name ? '#123A6D' : '#93A2B8', mr: 1 }} />
          <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: moveTargetInsurance === ins.name ? 700 : 400,
            color: moveTargetInsurance === ins.name ? '#0B2452' : '#0F1F3D' }}>
            {ins.name}
          </Typography>
          {moveTargetInsurance === ins.name && (
            <CheckCircleIcon sx={{ fontSize: 14, color: '#123A6D', ml: 'auto' }} />
          )}
        </Box>
      ))}
    </Box>

    <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem', py: 0.5 }}>
      Tests already assigned to the target insurance will be skipped automatically.
    </Alert>
  </DialogContent>

  <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid #f0f0f0' }}>
    <Button onClick={() => { setMoveDialogOpen(false); setMoveTargetInsurance(''); }}
      disabled={moving} variant="outlined"
      sx={{ color: '#5A6B87', borderColor: '#DDE5EF', flex: 1 }}>
      Cancel
    </Button>
    <Button variant="contained" onClick={handleMoveLabs}
      disabled={moving || !moveTargetInsurance}
      startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
      sx={{ flex: 1, bgcolor: '#123A6D', fontWeight: 700, '&:hover': { bgcolor: '#0B2452' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
      {moving ? 'Copying…' : `Copy ${selectedLabNames.size}`}
    </Button>
  </DialogActions>
</Dialog>

      <NewLabPricingDialog open={newPricingDialogOpen} onClose={() => setNewPricingDialogOpen(false)}
        selectedLab={selectedLab} insuranceProviders={insuranceProviders} loadingInsurance={loadingInsurance}
        onSaved={() => { if (selectedLab) loadInsuranceRecords(selectedLab); }} existingStatuses={existingStatuses} />

      <BulkApplyDialog
  open={bulkApplyDialogOpen}
  onClose={() => { setBulkApplyDialogOpen(false); setSourceRowForBulkApply(null); }}
  sourceRow={sourceRowForBulkApply}
  selectedRows={insuranceRows.filter(r => selectedRowIds.includes(getRowId(r)))}
  insuranceProviders={insuranceProviders}
  loadingInsurance={loadingInsurance}
  onSaved={() => { if (selectedLab) loadInsuranceRecords(selectedLab); }}
  existingStatuses={existingStatuses}
/>
    </>
  );
};

export default Laboratory;